import { useCallback, useEffect, useState } from "react";
import { api, type ClassroomStatus, type KitMeeting } from "./api";

// Google Classroom kit (Paul, Oct 13): every meeting of the course calendar with its lesson link
// (chabuildlab.com/lesson/<id> opens that lesson for whoever clicks it) and ready-to-paste text.
// Share to Classroom opens Classroom's own "create" page with the link; the teacher picks the class
// and schedules it there. Teachers only.
// Auto-post (decision 94): the owner connects Google Classroom, picks the class, and Sync now makes
// every upcoming lesson a draft assignment that Classroom posts at 7:30 am Central on its day (due
// 11:59 pm), and the Showcase as a scheduled announcement (decision 97). A lesson that spans several
// meetings is graded only on its last one; the earlier parts show "not graded" (Paul, Oct 28).
// Co-teachers see where it stands, read only.

const DAY = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
const dayLabel = (iso: string) => DAY.format(new Date(`${iso}T12:00:00Z`));
const AT = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
const atLabel = (utc: string) => AT.format(new Date(utc)).replace(" AM", " am").replace(" PM", " pm");
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** What Google's redirect back to Home (?classroom=…) means. */
const RETURNED: Record<string, string> = {
  connected: "Google Classroom is connected. Press Sync now (pick your class first if it asks).",
  denied: "You cancelled on Google's page. Nothing changed.",
  expired: "That connect link expired or was already used. Press Connect Google Classroom again.",
  scopes: "Google didn't give BuildLab both Classroom permissions. Connect again and leave both boxes ticked.",
  failed: "Connecting didn't work. Try again; if it keeps failing, CHA's IT may need to allow BuildLab's Classroom access.",
};

/** Reads and clears ?classroom=… (set by the Connect callback) so a reload doesn't repeat it. */
function takeReturned(): string | null {
  try {
    const url = new URL(window.location.href);
    const r = url.searchParams.get("classroom");
    if (!r) return null;
    url.searchParams.delete("classroom");
    window.history.replaceState(null, "", url.toString());
    return RETURNED[r] ?? null;
  } catch {
    return null;
  }
}

export function ClassroomKitEntry() {
  const [returned] = useState(takeReturned);
  const [open, setOpen] = useState(!!returned);
  return (
    <section aria-label="Google Classroom kit" className="subs-entry">
      <div>
        <h2>Google Classroom kit</h2>
        <p className="muted small">Every lesson on the course calendar, posted to Google Classroom for you or by hand: a link that opens it in BuildLab and the assignment text.</p>
      </div>
      <button type="button" className="outline" onClick={() => setOpen(true)}>
        Open the kit
      </button>
      {open && <ClassroomKitDialog onClose={() => setOpen(false)} returned={returned} />}
    </section>
  );
}

function ClassroomKitDialog({ onClose, returned }: { onClose: () => void; returned: string | null }) {
  const [meetings, setMeetings] = useState<KitMeeting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    void api.classroomKit().then((r) => setMeetings(r.meetings), (e: Error) => setError(e.message));
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1800);
    } catch {
      setError("Your browser blocked copying. Select the text and copy it instead.");
    }
  };
  const today = todayIso();

  return (
    <div className="testlab-backdrop" role="dialog" aria-modal="true" aria-label="Google Classroom kit" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="testlab classroom-kit">
        <div className="row between">
          <div>
            <span className="eyebrow">Course calendar · Mon/Wed 8:15</span>
            <h2>Google Classroom kit</h2>
          </div>
          <button type="button" className="icon-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <AutoPost returned={returned} />
        <h3 className="kit-manual-h">Post by hand</h3>
        <ol className="kit-how small">
          <li>
            <b>Share to Classroom</b> opens Classroom with the lesson link. Pick your class, choose <b>Assignment</b>, and paste the text (<b>Copy text</b>).
          </li>
          <li>
            Use <b>Schedule</b> in Classroom to post it on the lesson's date, before class (for example 7:30 am).
          </li>
          <li>Students who click the link sign in and land right in that lesson. Their work is saved in BuildLab; they turn it in with Submit there.</li>
        </ol>
        {error && <p className="dlg-error">{error}</p>}
        {!meetings && !error && <p className="muted">Loading the calendar…</p>}
        {meetings && (
          <ul className="kit-list">
            {meetings.map((m) => {
              const past = m.date < today;
              const key = `m${m.meeting}`;
              return (
                <li key={key} className={`kit-row${past ? " past" : ""}${m.date === today ? " today" : ""}`}>
                  <span className="kit-date">
                    <b>{dayLabel(m.date)}</b>
                    <span className="muted small">Meeting {m.meeting}</span>
                  </span>
                  <span className="kit-what">
                    <b>
                      {m.title}
                      {!m.graded && <NotGraded />}
                    </b>
                    <a className="small" href={m.url} target="_blank" rel="noopener noreferrer">
                      {m.url.replace(/^https?:\/\//, "")}
                    </a>
                  </span>
                  <span className="kit-actions">
                    <button type="button" className="outline small" onClick={() => void copy(key, `${m.title}\n\n${m.body}`)}>
                      {copied === key ? "Copied!" : "Copy text"}
                    </button>
                    <a className="primary small" href={`https://classroom.google.com/share?url=${encodeURIComponent(m.url)}`} target="_blank" rel="noopener noreferrer">
                      Share to Classroom
                    </a>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** An earlier part of a multi-meeting lesson: its exit ticket is submitted at the last part (Paul, Oct 28). */
const NotGraded = () => (
  <span className="tag kit-ungraded" title="Graded on the lesson's last meeting, where students submit the exit ticket for all parts.">
    not graded
  </span>
);

const STATE_TEXT: Record<ClassroomStatus["meetings"][number]["status"], (when: string) => string> = {
  posted: (w) => `Posted ${w}`,
  scheduled: (w) => `Scheduled for ${w}`,
  draft: () => "Draft in Classroom: post it there",
  failed: () => "Failed",
  "not synced": (w) => `Not synced yet (would post ${w})`,
  past: () => "Past",
};

/** Auto-post: connect, pick the class, Sync now, and each lesson's state in Classroom. */
function AutoPost({ returned }: { returned: string | null }) {
  const [s, setS] = useState<ClassroomStatus | null>(null);
  const [courses, setCourses] = useState<{ id: string; name: string; section: string | null }[] | null>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(returned);

  const load = useCallback(() => api.classroom().then(setS, (e: Error) => setError(e.message)), []);
  useEffect(() => void load(), [load]);
  const choosing = !!s?.connected && s.canChange && (!s.course || picking);
  useEffect(() => {
    if (choosing && !courses) void api.classroomCourses().then((r) => setCourses(r.courses), (e: Error) => setError(e.message));
  }, [choosing, courses]);

  const run = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const connect = () =>
    run("connect", async () => {
      const { url } = await api.classroomConnect();
      window.location.assign(url);
    });
  const pick = (id: string) =>
    run("pick", async () => {
      await api.classroomSetCourse(id);
      setPicking(false);
      setNotice(null);
      await load();
    });
  const sync = () =>
    run("sync", async () => {
      const { results } = await api.classroomSync();
      const failed = results.filter((r) => r.status === "failed").length;
      const done = results.filter((r) => r.status === "scheduled" || r.status === "draft").length;
      setNotice(failed ? `Synced ${done}; ${failed} failed (see below).` : `Synced ${done} lesson${done === 1 ? "" : "s"}. Classroom posts each one at 7:30 am on its day.`);
      await load();
    });
  const disconnect = () =>
    run("disconnect", async () => {
      if (!window.confirm("Disconnect Google Classroom? Assignments already in Classroom stay there; BuildLab stops updating them.")) return;
      await api.classroomDisconnect();
      setCourses(null);
      setNotice("Disconnected. BuildLab no longer has access to Google Classroom.");
      await load();
    });

  return (
    <section className="kit-auto" aria-label="Post to Google Classroom automatically">
      <h3>Auto-post to Google Classroom</h3>
      {notice && <p className="kit-notice small">{notice}</p>}
      {error && <p className="dlg-error">{error}</p>}
      {!s && !error && <p className="muted small">Checking Google Classroom…</p>}
      {s && !s.configured && (
        <p className="small muted">Not set up yet: Google Classroom posting needs a Google sign-in client for BuildLab (see "Google Classroom auto-post" in docs/DEPLOY.md). Until then, post lessons by hand below.</p>
      )}
      {s?.configured && !s.connected && (
        <div className="kit-auto-bar">
          {s.canChange ? (
            <>
              <button type="button" className="primary small" disabled={!!busy} onClick={() => void connect()}>
                {busy === "connect" ? "Opening Google…" : "Connect Google Classroom"}
              </button>
              <span className="muted small">Sign in with your school Google account and allow BuildLab to see your classes, create assignments and post announcements.</span>
            </>
          ) : (
            <span className="muted small">Not connected. The BuildLab owner connects Google Classroom.</span>
          )}
        </div>
      )}
      {s?.connected && (
        <>
          <div className="kit-auto-bar">
            {choosing ? (
              courses ? (
                courses.length ? (
                  <label className="small">
                    Class:{" "}
                    <select aria-label="Google Classroom class" defaultValue="" disabled={!!busy} onChange={(e) => e.target.value && void pick(e.target.value)}>
                      <option value="" disabled>
                        Pick your class…
                      </option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {c.section ? ` (${c.section})` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <span className="small">No active classes where you're a teacher in Google Classroom.</span>
                )
              ) : (
                <span className="muted small">Loading your classes…</span>
              )
            ) : (
              <span className="small">
                Class: <b>{s.course?.name ?? "not picked"}</b>
                {s.canChange && (
                  <button type="button" className="ghost small" onClick={() => setPicking(true)}>
                    Change
                  </button>
                )}
              </span>
            )}
            {s.canChange && s.course && !picking && (
              <button type="button" className="primary small" disabled={!!busy} onClick={() => void sync()}>
                {busy === "sync" ? "Syncing…" : "Sync now"}
              </button>
            )}
            {s.canChange && (
              <button type="button" className="outline small" disabled={!!busy} onClick={() => void disconnect()}>
                Disconnect
              </button>
            )}
          </div>
          {s.course && (
            <ul className="kit-auto-list" aria-label="Lessons in Google Classroom">
              {s.meetings.map((m) => (
                <li key={m.meeting}>
                  <span>
                    <b>{dayLabel(m.date)}</b> · {m.title}
                    {!m.graded && <NotGraded />}
                  </span>
                  <span className={`kit-state ${m.status.replace(" ", "-")}`}>
                    {m.link && m.status !== "past" ? (
                      <a href={m.link} target="_blank" rel="noopener noreferrer">
                        {STATE_TEXT[m.status](m.scheduledTime ? atLabel(m.scheduledTime) : "")}
                      </a>
                    ) : (
                      STATE_TEXT[m.status](m.scheduledTime ? atLabel(m.scheduledTime) : "")
                    )}
                    {m.error && <span className="small">: {m.error}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
